import React, { useState } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  Divider,
} from '@mui/material';
import { useToast } from '../contexts/ToastContext';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import { authService } from '@uniqcamp/shared';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { GateItem, NoticeItem, SchoolsResponse } from '../types';

const MAX_IMAGE_SIZE_BYTES = 500 * 1024;

function truncate(s: string, len: number) {
  if (!s || s.length <= len) return s || '—';
  return s.slice(0, len) + '…';
}

function NoticeFormFields({
  notice,
  gates,
  slideDurationSeconds,
  onChange,
  onImage,
  readOnly,
}: {
  notice: NoticeItem;
  gates: GateItem[];
  slideDurationSeconds: number;
  onChange: (field: keyof NoticeItem, value: string | string[] | boolean | number) => void;
  onImage: (file: File | null) => void;
  readOnly?: boolean;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      <TextField
        fullWidth
        size="small"
        multiline
        minRows={5}
        maxRows={12}
        label="Notice text"
        value={notice.text}
        onChange={(e) => onChange('text', e.target.value)}
        placeholder="e.g. Please have your ID ready"
        disabled={readOnly}
        sx={{ '& .MuiInputBase-root': { alignItems: 'flex-start' } }}
      />
      <TextField
        fullWidth
        size="small"
        type="number"
        label="Display duration (seconds)"
        value={notice.durationSeconds ?? slideDurationSeconds}
        onChange={(e) => onChange('durationSeconds', Math.min(60, Math.max(2, Number(e.target.value) || 2)))}
        inputProps={{ min: 2, max: 60 }}
        disabled={readOnly}
      />
      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Notice image (optional)</Typography>
        {notice.imageDataUrl ? (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
            <Box component="img" src={notice.imageDataUrl} alt="Notice" sx={{ maxWidth: 200, maxHeight: 120, objectFit: 'contain', border: 1, borderColor: 'divider', borderRadius: 1 }} />
            {!readOnly && <Button size="small" variant="outlined" color="secondary" onClick={() => onImage(null)}>Remove image</Button>}
          </Box>
        ) : (
          !readOnly && (
            <Button size="small" variant="outlined" component="label">
              Upload image
              <input type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) onImage(f); e.target.value = ''; }} />
            </Button>
          )
        )}
      </Box>
      <FormControl size="small" fullWidth disabled={readOnly}>
        <InputLabel>Show on gates</InputLabel>
        <Select
          multiple
          value={notice.gateIds}
          onChange={(e) => onChange('gateIds', e.target.value as string[])}
          input={<OutlinedInput label="Show on gates" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{(selected as string[]).map((gid) => { const g = gates.find((x) => x.gateId === gid); return <Chip key={gid} size="small" label={g?.name || gid} />; })}</Box>
          )}
        >
          {gates.map((g) => <MenuItem key={g.gateId} value={g.gateId}>{g.name || g.gateId}</MenuItem>)}
        </Select>
      </FormControl>
      <FormControlLabel
        control={<Switch size="small" checked={notice.showToParents !== false} onChange={(e) => onChange('showToParents', e.target.checked)} disabled={readOnly} />}
        label="Show during dispersal time"
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField size="small" label="Start date" type="date" value={notice.startDate ?? ''} onChange={(e) => onChange('startDate', e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 140 }} disabled={readOnly} />
        <TextField size="small" label="End date" type="date" value={notice.endDate ?? ''} onChange={(e) => onChange('endDate', e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 140 }} disabled={readOnly} />
        <TextField size="small" label="Start time" type="time" value={notice.startTime ?? ''} onChange={(e) => onChange('startTime', e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 120 }} disabled={readOnly || notice.showToParents !== false} />
        <TextField size="small" label="End time" type="time" value={notice.endTime ?? ''} onChange={(e) => onChange('endTime', e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 120 }} disabled={readOnly || notice.showToParents !== false} />
      </Box>
    </Box>
  );
}

function NoticeViewDetails({ notice, gates, slideDurationSeconds }: { notice: NoticeItem; gates: GateItem[]; slideDurationSeconds: number }) {
  const gateNames = (notice.gateIds || []).map((gid) => gates.find((x) => x.gateId === gid)?.name || gid);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography variant="caption" color="text.secondary">Status</Typography>
        <Box sx={{ mt: 0.5 }}>
          <Chip
            size="small"
            label={notice.active !== false ? 'Active' : 'Inactive'}
            color={notice.active !== false ? 'success' : 'default'}
            variant={notice.active !== false ? 'filled' : 'outlined'}
          />
        </Box>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">Notice text</Typography>
        <Typography variant="body1" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{notice.text || '—'}</Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">Display duration</Typography>
        <Typography variant="body1" sx={{ mt: 0.5 }}>{notice.durationSeconds ?? slideDurationSeconds} seconds</Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">Notice image</Typography>
        {notice.imageDataUrl ? (
          <Box component="img" src={notice.imageDataUrl} alt="Notice" sx={{ mt: 0.5, maxWidth: '100%', maxHeight: 200, objectFit: 'contain', border: 1, borderColor: 'divider', borderRadius: 1 }} />
        ) : (
          <Typography variant="body2" sx={{ mt: 0.5 }}>—</Typography>
        )}
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">Show on gates</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
          {gateNames.length === 0 ? '—' : gateNames.map((name) => <Chip key={name} size="small" label={name} variant="outlined" />)}
        </Box>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">Show during dispersal time</Typography>
        <Typography variant="body1" sx={{ mt: 0.5 }}>{notice.showToParents !== false ? 'Yes' : 'No'}</Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">Date range</Typography>
        <Typography variant="body1" sx={{ mt: 0.5 }}>
          {notice.startDate || notice.endDate ? `${notice.startDate || '—'} to ${notice.endDate || '—'}` : '—'}
        </Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">Time range</Typography>
        <Typography variant="body1" sx={{ mt: 0.5 }}>
          {notice.startTime || notice.endTime ? `${notice.startTime || '—'} to ${notice.endTime || '—'}` : '—'}
        </Typography>
      </Box>
    </Box>
  );
}

const Notices: React.FC = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { canEdit } = useFeatureAccess('notices');
  const [notices, setNotices] = useState<NoticeItem[]>([]);
  const [slideDurationSeconds, setSlideDurationSeconds] = useState<number>(5);
  const [addOpen, setAddOpen] = useState(false);
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [viewIdx, setViewIdx] = useState<number | null>(null);
  const [draftNotice, setDraftNotice] = useState<NoticeItem | null>(null);

  const { data: schoolsData } = useQuery<SchoolsResponse>('schools', async () => {
    const res = await apiClient.get('/schools');
    return res.data as SchoolsResponse;
  });
  const school = schoolsData?.schools?.[0];
  const gates: GateItem[] = school?.settings?.dispersalGates || school?.settings?.dismissalGates || [];

  React.useEffect(() => {
    if (school?.settings?.notices?.length) {
      setNotices(JSON.parse(JSON.stringify(school.settings.notices)));
    } else {
      setNotices([]);
    }
  }, [school?.settings?.notices]);

  React.useEffect(() => {
    const v = school?.settings?.noticeSlideDurationSeconds;
    setSlideDurationSeconds(typeof v === 'number' && v >= 2 ? v : 5);
  }, [school?.settings?.noticeSlideDurationSeconds]);

  const updateMutation = useMutation(
    async (payload: { _id: string; settings: Record<string, unknown> }) => {
      const res = await apiClient.put(`/schools/${payload._id}`, payload);
      return res.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('schools');
        showToast('success', 'Notices saved.');
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { message?: string } } };
        showToast('error', e.response?.data?.message ?? 'Failed to save');
      },
    }
  );

  const openAdd = () => {
    setDraftNotice({
      id: `notice-${Date.now()}`,
      text: '',
      gateIds: [],
      active: true,
      showToParents: true,
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      durationSeconds: slideDurationSeconds,
      imageDataUrl: '',
    });
    setAddOpen(true);
  };
  const confirmAdd = () => {
    if (!draftNotice) return;
    const newList = [...notices, { ...draftNotice }];
    setNotices(newList);
    setDraftNotice(null);
    setAddOpen(false);
    persistNotices(newList);
  };

  const removeNotice = (idx: number) => {
    if (!window.confirm('Remove this notice?')) return;
    const newList = notices.filter((_, i) => i !== idx);
    setNotices(newList);
    setEditIdx(null);
    setViewIdx(null);
    persistNotices(newList);
  };

  const updateNotice = (idx: number, field: keyof NoticeItem, value: string | string[] | boolean | number) => {
    setNotices((n) => {
      const next = [...n];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const toggleActive = (idx: number) => {
    const newList = notices.map((n, i) =>
      i === idx ? { ...n, active: n.active === false } : n
    );
    setNotices(newList);
    persistNotices(newList);
  };

  const persistNotices = (list: NoticeItem[]) => {
    if (!school) return;
    const defaultDuration = Math.min(60, Math.max(2, Math.round(slideDurationSeconds)));
    const normalizedNotices = list.map((n) => ({
      ...n,
      active: n.active !== false,
      showToParents: n.showToParents !== false,
      durationSeconds: typeof n.durationSeconds === 'number' ? Math.min(60, Math.max(2, Math.round(n.durationSeconds))) : undefined,
    }));
    updateMutation.mutate({
      _id: school._id,
      settings: {
        ...school.settings,
        notices: normalizedNotices,
        noticeSlideDurationSeconds: defaultDuration,
      },
    });
  };

  const handleNoticeImage = (idx: number, file: File | null) => {
    if (!file) {
      updateNotice(idx, 'imageDataUrl', '');
      return;
    }
    if (!file.type.startsWith('image/')) {
      showToast('error', 'Please select an image file (JPEG, PNG, etc.)');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      showToast('error', 'Image must be under 500KB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => updateNotice(idx, 'imageDataUrl', reader.result as string);
    reader.readAsDataURL(file);
  };

  if (!authService.getStoredUser()) return null;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="h1" fontWeight={600} sx={{ mb: 1 }}>
        Notices
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add notices to show on gate TV displays. Each notice can be assigned to one or more gates. Add, edit, and delete save immediately; use View to see details in the sidebar.
      </Typography>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
          <TextField
            size="small"
            type="number"
            label="Default duration (seconds)"
            value={slideDurationSeconds}
            onChange={(e) => setSlideDurationSeconds(Math.min(60, Math.max(2, Number(e.target.value) || 2)))}
            inputProps={{ min: 2, max: 60 }}
            sx={{ width: 160 }}
          />
          {canEdit && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
              Add notice
            </Button>
          )}
        </Box>

        {notices.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No notices yet. {canEdit ? 'Click Add notice to create one.' : ''}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Notice text</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Gates</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Dispersal time</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date range</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Image</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notices.map((notice, idx) => (
                  <TableRow key={notice.id} hover>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={notice.active !== false ? 'Active' : 'Inactive'}
                        color={notice.active !== false ? 'success' : 'default'}
                        variant={notice.active !== false ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>{truncate(notice.text, 60)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(notice.gateIds || []).length === 0
                          ? '—'
                          : (notice.gateIds || []).map((gid) => {
                              const g = gates.find((x) => x.gateId === gid);
                              return <Chip key={gid} size="small" label={g?.name || gid} variant="outlined" />;
                            })}
                      </Box>
                    </TableCell>
                    <TableCell>{notice.durationSeconds ?? slideDurationSeconds}s</TableCell>
                    <TableCell>{notice.showToParents !== false ? 'Yes' : 'No'}</TableCell>
                    <TableCell>
                      {notice.startDate || notice.endDate
                        ? `${notice.startDate || '—'} to ${notice.endDate || '—'}`
                        : '—'}
                    </TableCell>
                    <TableCell>{notice.imageDataUrl ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" title="View" onClick={() => setViewIdx(idx)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      {canEdit && (
                        <>
                          <IconButton
                            size="small"
                            title={notice.active !== false ? 'Set inactive' : 'Set active'}
                            onClick={() => toggleActive(idx)}
                            color={notice.active !== false ? 'inherit' : 'success'}
                          >
                            {notice.active !== false ? (
                              <ToggleOnIcon fontSize="small" />
                            ) : (
                              <ToggleOffIcon fontSize="small" />
                            )}
                          </IconButton>
                          <IconButton size="small" title="Edit" onClick={() => setEditIdx(idx)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" title="Delete" color="error" onClick={() => removeNotice(idx)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Add dialog */}
      <Dialog open={addOpen} onClose={() => { setAddOpen(false); setDraftNotice(null); }} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '90vh' } }}>
        <DialogTitle>Add notice</DialogTitle>
        <DialogContent>
          {draftNotice && (
            <NoticeFormFields
              notice={draftNotice}
              gates={gates}
              slideDurationSeconds={slideDurationSeconds}
              onChange={(field, value) => setDraftNotice((d) => d ? { ...d, [field]: value } : d)}
              onImage={(file) => {
                if (!file) setDraftNotice((d) => d ? { ...d, imageDataUrl: '' } : d);
                else {
                  if (!file.type.startsWith('image/')) { showToast('error', 'Please select an image file.'); return; }
                  if (file.size > MAX_IMAGE_SIZE_BYTES) { showToast('error', 'Image must be under 500KB.'); return; }
                  const reader = new FileReader();
                  reader.onload = () => setDraftNotice((d) => d ? { ...d, imageDataUrl: reader.result as string } : d);
                  reader.readAsDataURL(file);
                }
              }}
              readOnly={false}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddOpen(false); setDraftNotice(null); }}>Cancel</Button>
          {canEdit && <Button variant="contained" onClick={confirmAdd}>Add</Button>}
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editIdx !== null} onClose={() => setEditIdx(null)} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '90vh' } }}>
        <DialogTitle>Edit notice</DialogTitle>
        <DialogContent>
          {editIdx !== null && notices[editIdx] && (
            <NoticeFormFields
              notice={notices[editIdx]}
              gates={gates}
              slideDurationSeconds={slideDurationSeconds}
              onChange={(field, value) => updateNotice(editIdx, field, value)}
              onImage={(file) => handleNoticeImage(editIdx, file)}
              readOnly={false}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditIdx(null)}>Cancel</Button>
          {canEdit && (
            <Button variant="contained" onClick={() => { persistNotices(notices); setEditIdx(null); }} disabled={updateMutation.isLoading}>
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* View sidebar */}
      <Drawer
        anchor="right"
        open={viewIdx !== null}
        onClose={() => setViewIdx(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 400 } } }}
      >
        <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Notice details</Typography>
            <IconButton size="small" onClick={() => setViewIdx(null)} aria-label="Close"><CloseIcon /></IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {viewIdx !== null && notices[viewIdx] && (
            <NoticeViewDetails notice={notices[viewIdx]} gates={gates} slideDurationSeconds={slideDurationSeconds} />
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default Notices;
